import { ChangeDetectionStrategy, Component, TemplateRef, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { EnergyLabelsRegisteredChart } from '@cfp/shared/energy-labels';
import { PortfolioEnergyLabelRatings } from '@cfp/shared/portfolio/energy-label-ratings';
import { TranslateModule } from '@ngx-translate/core';
import {
  LabelGroupDTO,
  LabelsCertifiedResponseDTO,
  PortfolioBuildingsRoadmapBuildingDTO,
  PortfolioBuildingsRoadmapDTO,
  PortfoliosService
} from 'csr-asset-app';
import { combineLatest, mergeMap, of } from 'rxjs';
import { ReportBuildingsLegend, ReportPortfolioRoadmapTable } from '../../components';
import { ReportPage } from '../report-page';

const MAX_BUILDINGS_FIRST_PAGE = 10;
const MAX_BUILDINGS_ADDITIONAL_PAGES = 20;

@Component({
  selector: 'cfp-report-energy-labels-pages',
  templateUrl: 'report-energy-labels-pages.html',
  styleUrls: ['report-energy-labels-pages.scss'],
  imports: [
    PortfolioEnergyLabelRatings,
    ReportPage,
    ReportPortfolioRoadmapTable,
    ReportBuildingsLegend,
    EnergyLabelsRegisteredChart,
    TranslateModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ReportEnergyLabelsPages {
  public readonly portfolioId = input.required<number>();
  public readonly customerName = input.required<string>();
  public readonly buildingsWithPlan = input.required<PortfolioBuildingsRoadmapBuildingDTO[]>();
  public readonly portfolioBuildingsRoadmap = input.required<PortfolioBuildingsRoadmapDTO>();
  public readonly years = input.required<string[]>();
  public readonly hasBuildingsWithNoPlan = input.required<boolean>();
  public readonly translationKeyPrefix = input('portfolioBuildingsRoadmapReport');
  public readonly showLegend = input(true);
  public readonly headerLogo = input<string | undefined>(undefined);
  public readonly chromeFooter = input<TemplateRef<unknown> | undefined>(undefined);

  private readonly portfoliosService = inject(PortfoliosService);

  // For now this report only supports EPC
  protected labelGroup = LabelGroupDTO.EPC;

  private readonly labelsCertifiedFromApi = toSignal(
    combineLatest([toObservable(this.portfolioId), toObservable(this.portfolioBuildingsRoadmap)]).pipe(
      mergeMap(([portfolioId, portfolioBuildingsRoadmap]) => {
        if (!portfolioId || portfolioBuildingsRoadmap?.labelsCertified) {
          return of(undefined);
        }
        return this.portfoliosService.v2PortfoliosIdLabelscertifiedGet(portfolioId);
      })
    )
  );

  protected readonly labelsCertified = computed((): LabelsCertifiedResponseDTO | undefined => {
    const fromRoadmap = this.portfolioBuildingsRoadmap()?.labelsCertified;
    if (fromRoadmap) {
      return { certifiedLabelsDistribution: { [this.labelGroup]: fromRoadmap } };
    }
    return this.labelsCertifiedFromApi();
  });

  protected readonly pages = computed(() => {
    const buildingsWithPlan = this.buildingsWithPlan();
    if (!buildingsWithPlan) {
      return [];
    }

    const groups: PortfolioBuildingsRoadmapBuildingDTO[][] = [];
    let currentGroup: PortfolioBuildingsRoadmapBuildingDTO[] = [];
    let isFirstGroup = true;

    buildingsWithPlan.forEach((item) => {
      if (isFirstGroup && currentGroup.length === MAX_BUILDINGS_FIRST_PAGE) {
        groups.push(currentGroup);
        currentGroup = [];
        isFirstGroup = false;
      } else if (!isFirstGroup && currentGroup.length === MAX_BUILDINGS_ADDITIONAL_PAGES) {
        groups.push(currentGroup);
        currentGroup = [];
      }
      currentGroup.push(item);
    });

    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  });

  protected translationKey(suffix: string): string {
    // Content keys always use the shared namespace (LBG overrides via custom i18n).
    // translationKeyPrefix is only for report-page chrome title (buildings vs RETLL).
    return `portfolioBuildingsRoadmapReport.${suffix}`;
  }
}
